import { usePayItemSelectPayItemNoGeometriesQuery } from 'schema'
import { useOptions } from '../../hooks/useOptions'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { createOptionalContext } from 'utils/createContext'
import { toLabeledValues as toValues } from '../../utils/labeledValues'
import { PayItemSelectContext } from './types'
import { useProjectContext } from 'context/ProjectContext'

/**
 * @see {@link ToLabeledValuesOptions}
 */
const toLabeledValues: PayItemSelectContext['toLabeledValues'] = (records) =>
  toValues(records, {
    value: 'globalId',
    label: 'label',
    deleted: ({ deleted }) => deleted,
  })
/**
 * @see {@link PayItemSelectContext.fromLabeledValues}
 */
const fromLabeledValues: PayItemSelectContext['fromLabeledValues'] = (values) =>
  (values ?? []).map(({ value }) => value as string)

const fromLabeledValue: PayItemSelectContext['fromLabeledValue'] = (
  labeledValue
) => labeledValue?.value as string

const toLabeledValue: PayItemSelectContext['toLabeledValue'] = (record) =>
  record ? toLabeledValues([record])[0] : undefined

const { Provider, useContextFn } = createOptionalContext<PayItemSelectContext>({
  displayName: 'PayItemSelectContext',
  defaults: {
    payItems: [],
    __initialized: false,
    loading: false,
    options: [],
    toLabeledValues,
    fromLabeledValues,
    fromLabeledValue,
    toLabeledValue,
  },
  usingDefaults: ({ __initialized }) => !__initialized,
})

/**
 * returns the context associated with the {@link PayItemSelectProvider}
 *
 * warns if the PayItemSelectProvider is not found
 */
export const usePayItemSelectContext = useContextFn

/**
 * a provider to give access to data and methods associated with the PayItemSelect
 *
 * @param props
 * @param props.skip Skips the project pay-item query, supplying no pay items.
 */
const PayItemSelectProvider: React.FC<{ skip?: boolean }> = ({
  children,
  skip = false,
}) => {
  const { projectId } = useProjectContext()
  const { data, loading } = usePayItemSelectPayItemNoGeometriesQuery({
    ...DefaultQueryHookOptions,
    skip,
    variables: { globalId: projectId },
  })
  const options = useOptions({
    items: data?.payItemNoGeometries?.records,
    value: 'globalId',
    label: 'label',
    sort: 'label',
  })
  return (
    <Provider
      value={{
        payItems: data?.payItemNoGeometries?.records ?? [],
        loading,
        options,
        toLabeledValues,
        fromLabeledValues,
        toLabeledValue,
        fromLabeledValue,
        __initialized: true,
      }}
    >
      {children}
    </Provider>
  )
}

export default PayItemSelectProvider
